/**
 * StepForm — Step-level fields organized into card sections
 *
 * Modes:
 * - "wizard": renders one section at a time based on activeSection prop
 * - "editor": renders all sections stacked in the detail panel
 *
 * Sections: Basics, Trigger, Tracking Values, Conditions, Custom Fields, Debug
 */

import React, { useState } from 'react';
import HelpPanel from './HelpPanel';
import { SECTION_HELP, ATTRIBUTE_OPTIONS, attributeLabel, isCustomAttribute } from './utils/labelMapping';
import {
  CONDITION_OPERATORS, VALUE_OPERATORS, CONDITION_MODES,
} from './utils/configTransforms';

// ---- Section: Basics --------------------------------------------------------

function SectionBasics({ step, onUpdate, readOnly, showHelp }) {
  if (readOnly) {
    return (
      <div className="config-section-card">
        <div className="config-section-header">
          <span className="config-section-label">Basics</span>
        </div>
        <div className="form-row">
          <ReadField label="Step ID" value={step.step_id} mono />
          <ReadField label="Label" value={step.label} />
          <ReadField label="Application" value={step.application_name} />
          <ReadField label="URL Pattern" value={step.url_pattern} mono />
        </div>
      </div>
    );
  }

  return (
    <div className="config-section-card">
      <div className="config-section-header">
        <span className="config-section-label">Basics</span>
        {showHelp && <HelpPanel title={SECTION_HELP.steps.title} content={SECTION_HELP.steps.content} />}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Label</label>
          <input className="form-input" value={step.label}
            onChange={e => onUpdate('label', e.target.value)} placeholder="Invoice Submitted" />
        </div>
        <div className="form-group">
          <label className="form-label">Step ID</label>
          <input className="form-input mono" value={step.step_id}
            onChange={e => onUpdate('step_id', e.target.value)} placeholder="step-001" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Application Name</label>
          <input className="form-input" value={step.application_name}
            onChange={e => onUpdate('application_name', e.target.value)} placeholder="Acme ERP" />
        </div>
        <div className="form-group">
          <label className="form-label">URL Pattern</label>
          <input className="form-input mono" value={step.url_pattern}
            onChange={e => onUpdate('url_pattern', e.target.value)}
            placeholder="https://app.example.com/*" />
          <p className="form-hint">Use * as wildcard. Example: https://erp.acme.com/invoices/*</p>
        </div>
      </div>
    </div>
  );
}

// ---- Section: Trigger -------------------------------------------------------

function SectionTrigger({ step, onUpdate, readOnly, showHelp }) {
  if (readOnly) {
    return (
      <div className="config-section-card">
        <div className="config-section-header">
          <span className="config-section-label">Trigger</span>
        </div>
        <div className="form-row">
          <ReadField label="Click trigger" value={step.trigger_selector} mono />
          <ReadField label="Keyboard trigger" value={step.trigger_keyboard_selector} mono />
        </div>
      </div>
    );
  }

  return (
    <div className="config-section-card">
      <div className="config-section-header">
        <span className="config-section-label">Trigger</span>
        {showHelp && <HelpPanel title={SECTION_HELP.triggers.title} content={SECTION_HELP.triggers.content} />}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Click trigger</label>
          <input className="form-input mono" value={step.trigger_selector}
            onChange={e => onUpdate('trigger_selector', e.target.value)}
            placeholder="button#submit-invoice" />
          <p className="form-hint">CSS selector for the element to track clicks on. Right-click → Inspect in your browser to find it.</p>
        </div>
        <div className="form-group">
          <label className="form-label">Keyboard trigger <span className="text-tertiary" style={{ fontWeight: 400 }}>(optional)</span></label>
          <input className="form-input mono" value={step.trigger_keyboard_selector}
            onChange={e => onUpdate('trigger_keyboard_selector', e.target.value)}
            placeholder="input#search" />
          <p className="form-hint">CSS selector for an input that also triggers this step when Enter is pressed.</p>
        </div>
      </div>
    </div>
  );
}

// ---- Section: Tracking Values (Identifiers) ---------------------------------

function SectionTracking({ step, onUpdateIdentifier, onAddIdentifier, onRemoveIdentifier, readOnly, showHelp }) {
  if (readOnly) {
    return (
      <div className="config-section-card">
        <div className="config-section-header">
          <span className="config-section-label">Tracking Values</span>
          <span className="config-section-count">{step.identifiers.length}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {step.identifiers.map((id, ii) => (
            <span key={ii} className="config-chip mono">
              {id.label} → {id.selector} [{attributeLabel(id.attribute)}]
              {id.required && <span className="config-chip-badge">Required</span>}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="config-section-card">
      <div className="config-section-header">
        <span className="config-section-label">Tracking Values</span>
        <div className="flex items-center gap-8">
          {showHelp && <HelpPanel title={SECTION_HELP.tracking.title} content={SECTION_HELP.tracking.content} />}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onAddIdentifier}>
            + Add
          </button>
        </div>
      </div>
      <p className="form-hint" style={{ marginTop: 0, marginBottom: '12px' }}>
        {SECTION_HELP.trackingPerStep}
      </p>
      {step.identifiers.map((id, ii) => (
        <div key={ii} className="config-field-row">
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {ii === 0 && <label className="form-label">Label</label>}
            <input className="form-input mono" value={id.label}
              onChange={e => onUpdateIdentifier(ii, 'label', e.target.value)}
              placeholder="invoice_number" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {ii === 0 && <label className="form-label">CSS Selector</label>}
            <input className="form-input mono" value={id.selector}
              onChange={e => onUpdateIdentifier(ii, 'selector', e.target.value)}
              placeholder="#invoice-number" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {ii === 0 && <label className="form-label">Read from</label>}
            {isCustomAttribute(id.attribute) ? (
              <input className="form-input mono" value={id.attribute}
                onChange={e => onUpdateIdentifier(ii, 'attribute', e.target.value)}
                placeholder="data-value" />
            ) : (
              <select className="form-select" value={id.attribute}
                onChange={e => onUpdateIdentifier(ii, 'attribute', e.target.value)}>
                {ATTRIBUTE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                <option value="__custom">Custom attribute…</option>
              </select>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: ii === 0 ? '22px' : 0 }}>
            <label className="toggle" style={{ marginBottom: 0 }}>
              <input type="checkbox" checked={id.required}
                onChange={e => onUpdateIdentifier(ii, 'required', e.target.checked)} />
              <div className="toggle-track" /><div className="toggle-thumb" />
              <span className="toggle-label" style={{ fontSize: '12px' }}>Required</span>
            </label>
            <button type="button" className="btn btn-ghost btn-sm text-danger"
              onClick={() => onRemoveIdentifier(ii)}
              disabled={step.identifiers.length <= 1}
              title="Remove">×</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Section: Conditions ----------------------------------------------------

function SectionConditions({ step, onUpdateStep, onUpdateCondition, onAddCondition, onRemoveCondition, readOnly, showHelp, isAdmin }) {
  const [isExpanded, setIsExpanded] = useState(step.conditions.length > 0);

  if (readOnly) {
    if (step.conditions.length === 0) return null;
    return (
      <div className="config-section-card">
        <div className="config-section-header">
          <span className="config-section-label">Conditions</span>
          <span className="text-secondary" style={{ fontSize: '12px' }}>
            {CONDITION_MODES.find(m => m.value === step.conditions_mode)?.label || 'Only capture when…'}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {step.conditions.map((c, ci) => (
            <div
              key={ci}
              className="flex items-center gap-8"
              style={{ opacity: c.enabled ? 1 : 0.55 }}
            >
              {isAdmin && (
                <label className="toggle" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={c.enabled}
                    onChange={e => onUpdateCondition(ci, 'enabled', e.target.checked)}
                  />
                  <div className="toggle-track" /><div className="toggle-thumb" />
                </label>
              )}
              <span className="config-chip mono">
                {c.selector} [{c.attribute}] {c.operator}
                {VALUE_OPERATORS.includes(c.operator) ? ` "${c.value}"` : ''}
                {!c.enabled && <span className="config-chip-badge" style={{ color: 'var(--text-tertiary)', marginLeft: '8px' }}>Disabled</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!isExpanded && step.conditions.length === 0) {
    return (
      <div className="config-section-card config-section-collapsed" onClick={() => setIsExpanded(true)}>
        <div className="config-section-header">
          <span className="config-section-label" style={{ opacity: 0.5 }}>
            <span style={{ marginRight: '6px' }}>▸</span> Conditions
          </span>
          <span style={{ fontSize: '12px', opacity: 0.4 }}>None configured</span>
        </div>
      </div>
    );
  }

  return (
    <div className="config-section-card">
      <div className="config-section-header">
        <div className="flex items-center gap-8">
          <span className="config-section-label" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
            <span style={{ marginRight: '6px' }}>{isExpanded ? '▾' : '▸'}</span> Conditions
          </span>
          {showHelp && <HelpPanel title={SECTION_HELP.conditions.title} content={SECTION_HELP.conditions.content} />}
        </div>
        <div className="flex items-center gap-8">
          {step.conditions.length > 0 && (
            <select className="form-select" value={step.conditions_mode}
              onChange={e => onUpdateStep('conditions_mode', e.target.value)}
              style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}>
              {CONDITION_MODES.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { onAddCondition(); setIsExpanded(true); }}>
            + Add
          </button>
        </div>
      </div>
      {isExpanded && step.conditions.map((cond, ci) => (
        <div key={ci} className="config-field-row">
          <div style={{ display: 'flex', alignItems: 'center', paddingTop: ci === 0 ? '22px' : 0 }}>
            <label className="toggle" style={{ marginBottom: 0 }}>
              <input type="checkbox" checked={cond.enabled}
                onChange={e => onUpdateCondition(ci, 'enabled', e.target.checked)} />
              <div className="toggle-track" /><div className="toggle-thumb" />
            </label>
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {ci === 0 && <label className="form-label">CSS Selector</label>}
            <input className="form-input mono" value={cond.selector}
              onChange={e => onUpdateCondition(ci, 'selector', e.target.value)}
              placeholder="#status-field" disabled={!cond.enabled} />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {ci === 0 && <label className="form-label">Read from</label>}
            <select className="form-select" value={cond.attribute} disabled={!cond.enabled}
              onChange={e => onUpdateCondition(ci, 'attribute', e.target.value)}>
              {ATTRIBUTE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {ci === 0 && <label className="form-label">Operator</label>}
            <select className="form-select" value={cond.operator} disabled={!cond.enabled}
              onChange={e => {
                onUpdateCondition(ci, 'operator', e.target.value);
                if (!VALUE_OPERATORS.includes(e.target.value)) {
                  onUpdateCondition(ci, 'value', '');
                }
              }}>
              {CONDITION_OPERATORS.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>
          {VALUE_OPERATORS.includes(cond.operator) && (
            <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
              {ci === 0 && <label className="form-label">Value</label>}
              <input className="form-input mono" value={cond.value}
                onChange={e => onUpdateCondition(ci, 'value', e.target.value)}
                placeholder="expected value" disabled={!cond.enabled} />
            </div>
          )}
          <button type="button" className="btn btn-ghost btn-sm text-danger"
            onClick={() => onRemoveCondition(ci)} style={{ paddingTop: ci === 0 ? '22px' : 0 }}>
            ×
          </button>
        </div>
      ))}
      {isExpanded && step.conditions.length === 0 && (
        <p className="form-hint">No conditions configured. Events will always be captured for this step.</p>
      )}
    </div>
  );
}

// ---- Section: Custom Fields -------------------------------------------------

function SectionCustomFields({ step, onUpdateCustomField, onAddCustomField, onRemoveCustomField, readOnly, showHelp }) {
  const [isExpanded, setIsExpanded] = useState(step.custom_fields.length > 0);

  if (readOnly) {
    if (step.custom_fields.length === 0) return null;
    return (
      <div className="config-section-card">
        <div className="config-section-header">
          <span className="config-section-label">Custom Fields</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {step.custom_fields.map((cf, fi) => (
            <span key={fi} className="config-chip mono">
              {cf.key} → {cf.selector} [{attributeLabel(cf.attribute)}]
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (!isExpanded && step.custom_fields.length === 0) {
    return (
      <div className="config-section-card config-section-collapsed" onClick={() => setIsExpanded(true)}>
        <div className="config-section-header">
          <span className="config-section-label" style={{ opacity: 0.5 }}>
            <span style={{ marginRight: '6px' }}>▸</span> Custom Fields
          </span>
          <span style={{ fontSize: '12px', opacity: 0.4 }}>None configured</span>
        </div>
      </div>
    );
  }

  return (
    <div className="config-section-card">
      <div className="config-section-header">
        <div className="flex items-center gap-8">
          <span className="config-section-label" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
            <span style={{ marginRight: '6px' }}>{isExpanded ? '▾' : '▸'}</span> Custom Fields
          </span>
          {showHelp && <HelpPanel title={SECTION_HELP.customFields.title} content={SECTION_HELP.customFields.content} />}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { onAddCustomField(); setIsExpanded(true); }}>
          + Add
        </button>
      </div>
      {isExpanded && step.custom_fields.map((cf, fi) => (
        <div key={fi} className="config-field-row">
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {fi === 0 && <label className="form-label">Field Name</label>}
            <input className="form-input mono" value={cf.key}
              onChange={e => onUpdateCustomField(fi, 'key', e.target.value)}
              placeholder="department" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {fi === 0 && <label className="form-label">CSS Selector</label>}
            <input className="form-input mono" value={cf.selector}
              onChange={e => onUpdateCustomField(fi, 'selector', e.target.value)}
              placeholder="#dept-code" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            {fi === 0 && <label className="form-label">Read from</label>}
            <select className="form-select" value={cf.attribute}
              onChange={e => onUpdateCustomField(fi, 'attribute', e.target.value)}>
              {ATTRIBUTE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-ghost btn-sm text-danger"
            onClick={() => onRemoveCustomField(fi)} style={{ paddingTop: fi === 0 ? '22px' : 0 }}>
            ×
          </button>
        </div>
      ))}
      {isExpanded && step.custom_fields.length === 0 && (
        <p className="form-hint">No custom fields configured. Add fields to extract extra data from the page.</p>
      )}
    </div>
  );
}

// ---- Section: Debug ---------------------------------------------------------

function SectionDebug({ step, onUpdate, readOnly, isAdmin }) {
  if (readOnly) {
    // Non-admins: preserve previous behavior (hidden when off, warning-only when on).
    if (!isAdmin) {
      if (!step.debug) return null;
      return (
        <div className="config-section-card">
          <div className="alert alert-warning" style={{ marginBottom: 0 }}>
            Debug mode is enabled. Events will include DOM snapshots and override condition suppression.
          </div>
        </div>
      );
    }
    // Admins: always render with toggle so they can flip debug in place on published versions.
    return (
      <div className="config-section-card">
        <div className="config-section-header">
          <div>
            <span className="config-section-label">Debug Mode</span>
            <p className="form-hint" style={{ marginTop: '4px', marginBottom: 0 }}>
              {step.debug
                ? 'Debug is enabled. Events include DOM snapshots and override condition suppression.'
                : 'When enabled, conditions are overridden and events are always sent with a DOM snapshot.'}
            </p>
          </div>
          <label className="toggle" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={!!step.debug}
              onChange={e => onUpdate('debug', e.target.checked)}
            />
            <div className="toggle-track" /><div className="toggle-thumb" />
            <span className="toggle-label">{step.debug ? 'On' : 'Off'}</span>
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="config-section-card">
      <div className="config-section-header">
        <div>
          <span className="config-section-label">Debug Mode</span>
          <p className="form-hint" style={{ marginTop: '4px', marginBottom: 0 }}>
            When enabled, conditions are overridden and events are always sent with a DOM snapshot.
          </p>
        </div>
        <label className="toggle" style={{ marginBottom: 0 }}>
          <input type="checkbox" checked={step.debug}
            onChange={e => onUpdate('debug', e.target.checked)} />
          <div className="toggle-track" /><div className="toggle-thumb" />
          <span className="toggle-label">{step.debug ? 'On' : 'Off'}</span>
        </label>
      </div>
    </div>
  );
}

// ---- Read-only field helper -------------------------------------------------

function ReadField({ label, value, mono }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div className={`config-read-value${mono ? ' mono' : ''}`}>
        {value || '—'}
      </div>
    </div>
  );
}

// ---- Main StepForm ----------------------------------------------------------

export default function StepForm({
  step,
  processIndex,
  stepIndex,
  onUpdateStep,          // (field, value)
  onUpdateIdentifier,    // (identifierIndex, field, value)
  onAddIdentifier,       // ()
  onRemoveIdentifier,    // (identifierIndex)
  onUpdateCondition,     // (conditionIndex, field, value)
  onAddCondition,        // ()
  onRemoveCondition,     // (conditionIndex)
  onUpdateCustomField,   // (fieldIndex, field, value)
  onAddCustomField,      // ()
  onRemoveCustomField,   // (fieldIndex)
  mode = 'editor',       // 'wizard' | 'editor'
  activeSection = null,  // wizard only: 'basics' | 'trigger' | 'tracking' | 'conditions' | 'customFields'
  readOnly = false,
  showHelp = false,
  isAdmin = false,       // used in read mode to decide whether to render operational toggles
}) {
  // In wizard mode, only render the active section
  if (mode === 'wizard') {
    switch (activeSection) {
      case 'basics':
        return <SectionBasics step={step} onUpdate={onUpdateStep} readOnly={readOnly} showHelp={showHelp} />;
      case 'trigger':
        return <SectionTrigger step={step} onUpdate={onUpdateStep} readOnly={readOnly} showHelp={showHelp} />;
      case 'tracking':
        return (
          <SectionTracking step={step}
            onUpdateIdentifier={onUpdateIdentifier} onAddIdentifier={onAddIdentifier}
            onRemoveIdentifier={onRemoveIdentifier} readOnly={readOnly} showHelp={showHelp} />
        );
      case 'conditions':
        return (
          <>
            <SectionConditions step={step} onUpdateStep={onUpdateStep}
              onUpdateCondition={onUpdateCondition} onAddCondition={onAddCondition}
              onRemoveCondition={onRemoveCondition} readOnly={readOnly} showHelp={showHelp}
              isAdmin={isAdmin} />
            <SectionCustomFields step={step}
              onUpdateCustomField={onUpdateCustomField} onAddCustomField={onAddCustomField}
              onRemoveCustomField={onRemoveCustomField} readOnly={readOnly} showHelp={showHelp} />
          </>
        );
      default:
        return null;
    }
  }

  // Editor mode — render all sections stacked
  return (
    <div className="config-step-sections">
      <SectionBasics step={step} onUpdate={onUpdateStep} readOnly={readOnly} showHelp={showHelp} />
      <SectionTrigger step={step} onUpdate={onUpdateStep} readOnly={readOnly} showHelp={showHelp} />
      <SectionTracking step={step}
        onUpdateIdentifier={onUpdateIdentifier} onAddIdentifier={onAddIdentifier}
        onRemoveIdentifier={onRemoveIdentifier} readOnly={readOnly} showHelp={showHelp} />
      <SectionConditions step={step} onUpdateStep={onUpdateStep}
        onUpdateCondition={onUpdateCondition} onAddCondition={onAddCondition}
        onRemoveCondition={onRemoveCondition} readOnly={readOnly} showHelp={showHelp}
        isAdmin={isAdmin} />
      <SectionCustomFields step={step}
        onUpdateCustomField={onUpdateCustomField} onAddCustomField={onAddCustomField}
        onRemoveCustomField={onRemoveCustomField} readOnly={readOnly} showHelp={showHelp} />
      <SectionDebug step={step} onUpdate={onUpdateStep} readOnly={readOnly} isAdmin={isAdmin} />
    </div>
  );
}
